import { computed, getCurrentInstance, onUnmounted, ref, watch, type Ref } from 'vue';
import { useRouter } from 'vue-router';
import {
  isSortOrder,
  type OrderOptions,
  type SearchFilter,
  type SearchParams,
  type SortOrder,
} from '@/shared/lib';
import { useInfiniteScrollObserver } from '@/shared/lib/use';
import { SHOW_ALL_ITEMS_QUANTITY } from '@/shared/config';
import {
  hasEligibleRevision,
  toRecordItem,
  useEligibleMatricesStore,
  type RecordItem,
  type RecordRevision,
} from '@/entities/Record';
import { useRecordSelection } from './useRecordSelection';
import { createAddRecordFilterContext, type AddRecordFilters } from './addRecordFilterContext';
import { DEFAULT_RECORD_PAGE_SIZE, RECORD_QUANTITY_OPTIONS } from '../config/pagination';

export type RecordSearchField = 'code' | 'title';

// The route lives in the app layer, which a feature cannot import, so the name is repeated here.
const DIRECTORY_RECORD_VIEW_ROUTE_NAME = 'DiretoriosVerRegistro';

type SortField = 'homologationDate' | 'title';

const ORDER_OPTIONS: OrderOptions[] = [
  { label: 'Por mais recente', sort: 'homologationDate', order: 'desc' },
  { label: 'Por mais antigo', sort: 'homologationDate', order: 'asc' },
  { label: 'Por ordem alfabética crescente', sort: 'title', order: 'asc' },
  { label: 'Por ordem alfabética decrescente', sort: 'title', order: 'desc' },
];

/** The listing sorts by these; the modal's own field names are mapped on the way out. */
const SORT_FIELD_BY_MODAL_FIELD: Record<SortField, string> = {
  homologationDate: 'effectiveDate',
  title: 'name',
};

const SEARCH_TYPE_BY_FIELD: Record<RecordSearchField, 'prefix' | 'name'> = {
  code: 'prefix',
  title: 'name',
};

/** A revision and the record it belongs to, kept for the rows the user actually picked. */
export interface RecordSelection {
  record: RecordItem;
  revision: RecordRevision;
}

function isSortField(value: string): value is SortField {
  return value === 'homologationDate' || value === 'title';
}

function sortOlderRevisionsDescending(revisions: RecordRevision[]): RecordRevision[] {
  return [...revisions].sort((a, b) => b.revision - a.revision);
}

/**
 * Turns the modal's filter panel into query filters the eligible listing understands.
 * The listing reads `elaborationDate` as the effective date of the matrix revision on the row, and
 * the DTO drops any other name, so the "Data de Vigência" field keeps sending this key.
 */
function toServerFilters(filters: AddRecordFilters): SearchFilter {
  return {
    elaborationDate: filters.createdAt ?? undefined,
    revision: filters.revision && filters.revision > 0 ? filters.revision : undefined,
  };
}

export interface UseAddRecordModalOptions {
  /**
   * Endpoint of the eligible matrices for the selected container. Search, ordering, filtering and
   * pagination all happen there: the modal holds one page at a time.
   */
  searchUrl: Ref<string | null>;
  /** Upper bound on selected revisions, mirroring the link DTO's `@ArrayMaxSize`. */
  maxSelections?: number;
  selectionLimitMessage?: string;
}

export function useAddRecordModal({
  searchUrl,
  maxSelections,
  selectionLimitMessage,
}: UseAddRecordModalOptions) {
  const store = useEligibleMatricesStore();
  const router = useRouter();

  const searchFields = [
    { id: 'code' as const, name: 'Registro' },
    { id: 'title' as const, name: 'Título' },
  ];

  const searchTerm = ref('');
  const searchFieldId = ref<RecordSearchField>('code');
  const DEFAULT_SORT: { sort: SortField; order: SortOrder } = {
    sort: 'homologationDate',
    order: 'desc',
  };
  const sort = ref<{ sort: SortField; order: SortOrder }>({ ...DEFAULT_SORT });
  const selectedSortField = ref('');
  const selectedSortOrder = ref('');

  const selectedQuantity = ref<number>(DEFAULT_RECORD_PAGE_SIZE);
  const currentPage = ref(1);
  const expandedRecordIds = ref<Set<number>>(new Set());

  const filterContext = createAddRecordFilterContext(() => {
    void loadRecords(1);
  });
  const { activeFilters, isFilterActive, isFilterPanelOpen } = filterContext;

  const {
    selectedRevisionIdList,
    hasSelectedRevisions,
    isRevisionSelected,
    toggleRevisionSelection: toggleSelectionId,
    toggleAllRevisions,
    selectionLimitError,
    canSelectRevision,
    clearSelectionLimitError,
  } = useRecordSelection({ maxSelections, selectionLimitMessage });

  const isShowAllMode = computed(() => selectedQuantity.value === SHOW_ALL_ITEMS_QUANTITY);

  const sourceRecords = computed(() =>
    isShowAllMode.value ? store.allRevisions : store.revisions
  );

  const recordRows = computed<RecordItem[]>(() =>
    sourceRecords.value.filter(hasEligibleRevision).map(toRecordItem)
  );

  const totalItems = computed(() => store.metadata?.total ?? 0);
  const totalRecordsLabel = computed(
    () =>
      `${totalItems.value.toLocaleString('pt-BR')} ${totalItems.value === 1 ? 'Registro' : 'Registros'}`
  );

  const loading = computed(() => store.loading);
  const loadingMore = computed(() => store.loadingMore);
  const hasMoreItems = computed(() => isShowAllMode.value && store.hasMoreRevisions);
  const metadata = computed(() => store.metadata);

  function buildSearchParams(page: number): SearchParams {
    return {
      search: searchTerm.value,
      searchType: SEARCH_TYPE_BY_FIELD[searchFieldId.value],
      sort: {
        sort: SORT_FIELD_BY_MODAL_FIELD[sort.value.sort],
        order: sort.value.order,
      },
      filter: toServerFilters(activeFilters.value),
      page,
      amount: selectedQuantity.value,
      searchUrl: searchUrl.value ?? '',
    };
  }

  let isRequestInFlight = false;
  let queuedJob: (() => Promise<void>) | null = null;

  /**
   * One request at a time, and the last one asked for is the one that lands. The store writes its
   * own state, so a request id could not undo a stale write: two fast searches resolving out of
   * order would paint the older answer, and a scroll batch landing after a new search would append
   * to a list that no longer exists. Queueing the tail job instead keeps the table showing what the
   * user last asked for — the job reads the current params when it finally runs.
   */
  async function runExclusive(job: () => Promise<void>): Promise<void> {
    if (isRequestInFlight) {
      queuedJob = job;
      return;
    }

    isRequestInFlight = true;
    try {
      await job();
      while (queuedJob !== null) {
        const nextJob = queuedJob;
        queuedJob = null;
        await nextJob();
      }
    } finally {
      isRequestInFlight = false;
    }
  }

  async function runSearch(page: number): Promise<void> {
    clearSelectionLimitError();
    store.setQuantity(selectedQuantity.value);
    await store.performSearch(buildSearchParams(page), page);
    pruneExpandedRecords();
    syncSelectedRevisions();
  }

  async function loadRecords(page = 1): Promise<void> {
    currentPage.value = page;

    if (!searchUrl.value) {
      return;
    }

    await runExclusive(() => runSearch(page));
  }

  /** Next scroll batch, under the same one-at-a-time rule as a fresh search. */
  async function loadMoreRecords(): Promise<void> {
    await runExclusive(async () => {
      await store.loadMoreRevisions();
      syncSelectedRevisions();
    });
  }

  function handleSearch(nextSearchTerm: string): void {
    searchTerm.value = nextSearchTerm;
    void loadRecords(1);
  }

  function handleSort(sortField: string, orderFlow: string): void {
    if (isSortField(sortField) && isSortOrder(orderFlow)) {
      sort.value = { sort: sortField, order: orderFlow };
      selectedSortField.value = sortField;
      selectedSortOrder.value = orderFlow;
    } else {
      sort.value = { ...DEFAULT_SORT };
      selectedSortField.value = '';
      selectedSortOrder.value = '';
    }
    void loadRecords(1);
  }

  function handleQuantityChange(quantity: number): void {
    selectedQuantity.value = quantity;
    void loadRecords(1);
  }

  function handlePageChange(page: number): void {
    void loadRecords(page);
  }

  watch(searchFieldId, () => {
    if (searchTerm.value.trim() === '') {
      void loadRecords(1);
    }
  });

  useInfiniteScrollObserver({
    enabled: isShowAllMode,
    loadingMore,
    hasMore: computed(() => store.hasMoreRevisions),
    onLoadMore: loadMoreRecords,
  });

  const pageRevisions = computed<RecordSelection[]>(() =>
    recordRows.value.flatMap((record) =>
      [record.latestRevision, ...record.olderRevisions].map((revision) => ({ record, revision }))
    )
  );

  /**
   * The revisions the user picked, kept even after their page is gone. Without this the submit
   * would only ever see what is on screen, and a selection made two pages back would be dropped
   * on the way to the request — silently, because the ids alone still look like a full batch.
   */
  const selectedRevisions = ref<Map<number, RecordSelection>>(new Map());

  function syncSelectedRevisions(): void {
    const next = new Map<number, RecordSelection>();

    selectedRevisionIdList.value.forEach((revisionId) => {
      const known =
        selectedRevisions.value.get(revisionId) ??
        pageRevisions.value.find((candidate) => candidate.revision.id === revisionId);

      if (known) {
        next.set(revisionId, known);
      }
    });

    selectedRevisions.value = next;
  }

  const selectedRevisionList = computed<RecordSelection[]>(() =>
    selectedRevisionIdList.value
      .map((revisionId) => selectedRevisions.value.get(revisionId))
      .filter((selection): selection is RecordSelection => selection !== undefined)
  );

  function toggleRevisionSelection(revisionId: number): void {
    toggleSelectionId(revisionId);
    syncSelectedRevisions();
  }

  const visibleLatestRevisionIds = computed<number[]>(() =>
    recordRows.value.map((record) => record.latestRevision.id)
  );

  const allVisibleSelected = computed(
    () =>
      visibleLatestRevisionIds.value.length > 0 &&
      visibleLatestRevisionIds.value.every((revisionId) => isRevisionSelected(revisionId))
  );

  const isSelectionIndeterminate = computed(
    () =>
      !allVisibleSelected.value &&
      visibleLatestRevisionIds.value.some((revisionId) => isRevisionSelected(revisionId))
  );

  /**
   * Scoped to the page, like the document modal's own select-all: with server-side pagination
   * "todos" cannot mean the whole eligible universe, and clearing everything would throw away
   * picks the user made on pages that are no longer on screen.
   */
  function handleToggleAllVisible(): void {
    toggleAllRevisions(visibleLatestRevisionIds.value);
    syncSelectedRevisions();
  }

  function pruneExpandedRecords(): void {
    const pageRecordIds = new Set(recordRows.value.map((record) => record.id));
    expandedRecordIds.value = new Set(
      [...expandedRecordIds.value].filter((recordId) => pageRecordIds.has(recordId))
    );
  }

  function isRecordExpanded(recordId: number): boolean {
    return expandedRecordIds.value.has(recordId);
  }

  function toggleRecordExpansion(recordId: number): void {
    if (expandedRecordIds.value.has(recordId)) {
      expandedRecordIds.value.delete(recordId);
    } else {
      expandedRecordIds.value.add(recordId);
    }
    expandedRecordIds.value = new Set(expandedRecordIds.value);
  }

  function getSortedOlderRevisions(record: RecordItem): RecordRevision[] {
    return sortOlderRevisionsDescending(record.olderRevisions);
  }

  function viewRecordRevision(revision: RecordRevision): void {
    const { href } = router.resolve({
      name: DIRECTORY_RECORD_VIEW_ROUTE_NAME,
      params: { id: revision.id },
    });
    window.open(href, '_blank', 'noopener');
  }

  watch(searchUrl, () => {
    store.clearCache();
    void loadRecords(1);
  });

  if (getCurrentInstance()) {
    onUnmounted(() => {
      store.clearCache();
    });
  }

  void loadRecords(1);

  return {
    searchFields,
    searchFieldId,
    searchTerm,
    orderOptions: ORDER_OPTIONS,
    sortField: selectedSortField,
    sortOrder: selectedSortOrder,
    recordRows,
    handleSearch,
    handleSort,

    filterContext,
    activeFilters,
    isFilterActive,
    isFilterPanelOpen,
    toggleFilterPanel: filterContext.toggleFilterPanel,
    closeFilterPanel: filterContext.closeFilterPanel,
    handleFilterApply: filterContext.applyFilters,

    quantityOptions: RECORD_QUANTITY_OPTIONS,
    selectedQuantity,
    currentPage,
    totalItems,
    totalRecordsLabel,
    metadata,
    isShowAllMode,
    hasMoreItems,
    loading,
    loadingMore,
    loadRecords,
    handleQuantityChange,
    handlePageChange,

    hasSelectedRevisions,
    selectedRevisionIdList,
    selectedRevisionList,
    visibleLatestRevisionIds,
    allVisibleSelected,
    isSelectionIndeterminate,
    isRevisionSelected,
    toggleRevisionSelection,
    handleToggleAllVisible,
    selectionLimitError,
    canSelectRevision,

    isRecordExpanded,
    toggleRecordExpansion,
    getSortedOlderRevisions,
    viewRecordRevision,
  };
}
